/**
 * Consequence model — the GENERIC, unit-agnostic wire shapes for "what a single agent
 * action does" and the frozen per-grant policy that gates it. The single source of truth
 * for every surface (admin AND portal) that reads or writes an agent consequence policy;
 * mirrors the Go leaf package core/identity/consequence/consequence.go for
 * every field an operator can author here. Fields the form editor does NOT author
 * (qualifier cells, cumulative_deny) are parsed and carried so a round-trip never
 * silently strips them — policyHasAdvancedRules() gates the editors closed on them.
 *
 * The model knows ZERO units: a consequence is a NUMBER plus a STRING that says what the
 * number is. Money is just one DIMENSION (key "usd", quantity in minor units); IOCs,
 * alerts, shares, MWh are other dimensions. The dimension key is operator-entered free
 * text — never a money widget. Adding a dimension is data, never a type change.
 *
 *   - Consequence: the runtime, per-action description — a list of Measurement
 *     (dimension + non-negative quantity). What the human is shown / the action_hash binds.
 *   - ConsequencePolicy: the frozen-at-mint gating policy — a list of per-dimension
 *     Threshold. The admin config forms set BOTH the per-action cap (offline gate) and
 *     the optional cumulative cap (online, windowed, cross-grant ledger gate).
 */

import { z } from "zod";

import { formatValue, type DimMeta } from "./consequence-format";

// dimensionKeyRe is the canonical dimension-key shape, matching the Go regex on
// consequence.dimensionKeyRe AND the SQL CHECK on consequence_dimension.key: a lowercase
// identifier, 1..64 chars. Keys are append-only at storage; here we enforce well-formedness.
export const dimensionKeyRe = /^[a-z][a-z0-9_]{0,63}$/;

/** A dimension key validator with a human-readable message for the config forms. */
export const dimensionKeySchema = z
  .string()
  .regex(
    dimensionKeyRe,
    "A dimension is a lowercase key (a–z, 0–9, _), starting with a letter — e.g. usd, iocs_enriched.",
  );

// ── Window / scope (cumulative gate — backend-only for v1; modeled so a read parses) ──

// DISPLAY-ENUM TOLERANCE: a window kind is metadata the client only renders, and the backend may add
// a new kind before the frontend ships a label. A strict z.enum would throw on that one value and fail
// the WHOLE query (blanking the page); .catch("") degrades an unknown value to "" (no window shown)
// instead. Reserve strict enums for values that gate security (those SHOULD fail closed). The fallback
// is an enum member, so the inferred TS type is unchanged. Apply this pattern to every DISPLAY enum.
export const windowKindSchema = z.enum([
  "",
  "rolling",
  "calendar_day",
  "calendar_month",
  "delegation_lifetime",
]).catch("");
export type WindowKind = z.infer<typeof windowKindSchema>;

export const consequenceScopeSchema = z.enum([
  "",
  "agent",
  "delegation",
  "standing_delegation",
  "actor_human",
  "tenant",
  "workload",
  // The consent-anchored reset-proof scope: an agent-REQUESTED consent pools its cumulative budget
  // on the human's consent_id (invariant I4). The backend emits this on any standing whose cumulative
  // cap is consent-anchored (e.g. the crew lead's agent-requested standing); omitting it here made the
  // portal's standing/consent/request parse throw and the "Agents" page fail to load.
  "consent",
  // DISPLAY-ENUM TOLERANCE (see the comment on windowKindSchema): the scope is metadata the portal
  // only shows, so a backend value the frontend hasn't shipped yet must NOT fail the whole query —
  // it falls back to "" (unknown/no-scope) instead of throwing. The fallback is an enum member, so
  // the inferred type is unchanged.
]).catch("");
export type ConsequenceScope = z.infer<typeof consequenceScopeSchema>;

// Window mirrors the Go `Window` struct (window_kind / window_seconds, both omitempty). A
// per-action-only threshold encodes an empty window; the backend tolerates an absent object.
// Both fields are optional (not defaulted) so the OUTPUT type permits an omitted field — the
// exact wire shape the editor emits: window_seconds is present ONLY for a rolling window, and
// window_kind is absent on a window-less ({}) object the Go encoder may send.
export const consequenceWindowSchema = z.object({
  window_kind: windowKindSchema.optional(),
  window_seconds: z.number().int().nonnegative().optional(),
});
export type ConsequenceWindow = z.infer<typeof consequenceWindowSchema>;

// ── Threshold (one per-dimension gating rule) ───────────────────────────────────────────
//
// Mirrors the Go `Threshold`. per_action_max / cumulative_max / per_action_hard_max are
// nullable so an UN-SIZED dimension is distinct from a zero cap. Band semantics (Go
// consequence.go): quantity ≤ per_action_max runs autonomously; above it ESCALATES to a
// live human approval; above per_action_hard_max the action is DENIED — no approval can
// authorize it (the PEP enforces the hard ceiling BEFORE the approval lookup). Invariant
// (enforced by Go Validate and mirrored in draftsToPolicy): per_action_max ≤ per_action_hard_max.
export const consequenceThresholdSchema = z.object({
  dimension: dimensionKeySchema,
  per_action_max: z.number().int().nonnegative().nullable().optional(),
  per_action_hard_max: z.number().int().nonnegative().nullable().optional(),
  cumulative_max: z.number().int().nonnegative().nullable().optional(),
  window: consequenceWindowSchema.optional(),
  scope: consequenceScopeSchema.optional(),
  // Enforcement-bearing fields the FORM EDITOR does not author but must never silently
  // strip on a read-edit-write round-trip (an API-authored policy can carry them):
  //   - cumulative_deny: breach of the cumulative cap DENIES instead of escalating;
  //   - qualifier: compound-cap cell predicates (S3) — a qualified threshold applies only
  //     to calls matching its cell, and is step-up-only per the Go Validate rules.
  // Parsed and carried so policyHasAdvancedRules() can fail the editors CLOSED (read-only)
  // rather than re-submitting a policy with these rules erased.
  cumulative_deny: z.boolean().optional(),
  qualifier: z.array(z.unknown()).optional(),
  // The FROZEN, tighten-only ceiling on how much a dual-control "temporary budget increase"
  // may add to this cumulative cap within one window (smallest units). null/absent ⇒ no
  // raises are permitted (deny-by-default). Meaningful only with a cumulative_max. Authored
  // in the editor; enforced by the raise ceremony at approve time.
  max_supplement_per_window: z.number().int().nonnegative().nullable().optional(),
});
export type ConsequenceThreshold = z.infer<typeof consequenceThresholdSchema>;

// ── Policy (the frozen-at-mint gating policy) ───────────────────────────────────────────
//
// An EMPTY policy (no thresholds) is deny-biased: every dimension is un-sized ⇒ escalate-all.
export const consequencePolicySchema = z.object({
  thresholds: z.array(consequenceThresholdSchema).optional().default([]),
});
export type ConsequencePolicy = z.infer<typeof consequencePolicySchema>;

// ── Consequence (the runtime, per-action description) ───────────────────────────────────

export const measurementSchema = z.object({
  dimension: dimensionKeySchema,
  quantity: z.number().int().nonnegative(),
});
export type Measurement = z.infer<typeof measurementSchema>;

export const consequenceSchema = z.object({
  measurements: z.array(measurementSchema).optional().default([]),
});
export type Consequence = z.infer<typeof consequenceSchema>;

// ── Form helpers (the per-dimension threshold editor the config dialogs use) ─────────────

/**
 * A single editable row in the policy editor: a dimension key, a per-action cap, an
 * OPTIONAL hard per-action ceiling, and an OPTIONAL cumulative cap (a windowed,
 * cross-grant ledger gate). All fields are raw input strings. A row may carry any
 * non-empty combination of the three caps (≥1 required).
 *
 *   - perActionMax: quantity above it ESCALATES to live human approval.
 *   - perActionHardMax: quantity above it is DENIED — even with approval. When both are
 *     set, perActionMax ≤ perActionHardMax (the Go band-ordering invariant).
 *
 * The cumulative fields are the raw form state behind the optional "cumulative cap"
 * expander: an entirely-blank cumulative section (cumulativeMax, windowKind, scope, and
 * windowSeconds all empty) means "no cumulative cap" and is dropped from the wire.
 *
 *   - windowKind ∈ {"", "rolling", "calendar_day", "calendar_month", "delegation_lifetime"}
 *   - scope      ∈ {"", "agent", "delegation", "standing_delegation", "actor_human", "tenant"}.
 *     A HARD STOP (cumulativeDeny) is restricted to the reset-proof scopes actor_human/tenant —
 *     the identity-scoped keys reset on a retire+re-provision (see draftsToPolicy).
 *   - windowSeconds is required (>0) iff windowKind === "rolling".
 */
export interface ThresholdDraft {
  dimension: string;
  perActionMax: string;
  perActionHardMax: string;
  cumulativeMax: string;
  windowKind: WindowKind;
  windowSeconds: string;
  scope: ConsequenceScope;
  // When the cumulative budget is exhausted: false ⇒ escalate (ask a person, the default — a human can
  // approve past it); true ⇒ HARD STOP (deny; no approval can authorize past it, only a deliberate
  // budget raise). Meaningful only when a cumulative cap is set.
  cumulativeDeny: boolean;
  // The FROZEN per-window ceiling on a dual-control "temporary budget increase" to this cap (raw human
  // units, like the cap fields). "" ⇒ no raises permitted (deny-by-default). Meaningful only with a
  // cumulative cap; the editor also offers a "25% of base" helper that fills this from cumulativeMax.
  maxSupplementPerWindow: string;
}

/** A fresh, empty draft row (per-action only; no hard ceiling, no cumulative cap). */
export function emptyThresholdDraft(): ThresholdDraft {
  return {
    dimension: "",
    perActionMax: "",
    perActionHardMax: "",
    cumulativeMax: "",
    windowKind: "",
    windowSeconds: "",
    scope: "",
    cumulativeDeny: false,
    maxSupplementPerWindow: "",
  };
}

/** True iff the row has no cumulative cap and no leftover window/scope/seconds typed in. */
function cumulativeSectionEmpty(d: ThresholdDraft): boolean {
  return (
    (d.cumulativeMax ?? "").trim() === "" &&
    (d.windowKind ?? "") === "" &&
    (d.scope ?? "") === "" &&
    (d.windowSeconds ?? "").trim() === "" &&
    (d.maxSupplementPerWindow ?? "").trim() === ""
  );
}

/**
 * Seed editor rows from a policy read off the wire (empty → one blank row to type into). When a
 * `dimMeta` map is supplied, each cap is shown in HUMAN units (a currency's decimals via its exponent)
 * so the editor and the read view agree; without it the raw smallest-unit integer is shown, the
 * pre-flip behavior. Pairs with draftsToPolicy(drafts, dimMeta) for a lossless round-trip per exponent.
 */
export function policyToDrafts(
  policy: ConsequencePolicy | null | undefined,
  dimMeta?: Map<string, DimMeta>,
): ThresholdDraft[] {
  const rows = (policy?.thresholds ?? []).map((t) => {
    const meta = dimMeta?.get(t.dimension);
    return {
      dimension: t.dimension,
      perActionMax: t.per_action_max != null ? minorToDraftString(t.per_action_max, meta) : "",
      perActionHardMax:
        t.per_action_hard_max != null ? minorToDraftString(t.per_action_hard_max, meta) : "",
      cumulativeMax: t.cumulative_max != null ? minorToDraftString(t.cumulative_max, meta) : "",
      windowKind: (t.window?.window_kind ?? "") as WindowKind,
      windowSeconds:
        t.window?.window_kind === "rolling" && t.window?.window_seconds
          ? String(t.window.window_seconds)
          : "",
      scope: (t.scope ?? "") as ConsequenceScope,
      cumulativeDeny: t.cumulative_deny === true,
      maxSupplementPerWindow:
        t.max_supplement_per_window != null ? minorToDraftString(t.max_supplement_per_window, meta) : "",
    };
  });
  return rows.length > 0 ? rows : [emptyThresholdDraft()];
}

export interface DraftsToPolicyResult {
  /** The built policy, or null when no usable rows were entered (caller decides: clear vs. error). */
  policy: ConsequencePolicy | null;
  /**
   * Field errors keyed by row index, sub-keys joined as `${i}.${field}`. Sub-keys:
   * "dimension", "perActionMax", "perActionHardMax", "cumulativeMax", "windowKind",
   * "windowSeconds", "scope".
   */
  errors: Record<string, string>;
}

/**
 * Build a wire policy from the editor rows, mirroring consequence.ConsequencePolicy.Validate
 * in the Go backend so the client never POSTs a body the strict handler 400s. For each
 * non-empty row:
 *   - the dimension key must be well-formed and unique;
 *   - at least one cap (per-action, hard per-action, OR cumulative) must be set;
 *   - a per-action cap / hard ceiling, if present, is a non-negative integer;
 *   - when both per-action caps are set, perActionMax ≤ perActionHardMax (the Go band
 *     ordering: autonomous ≤ step-up band ≤ hard-deny);
 *   - the cumulative section is all-or-nothing: a cumulative cap requires a window kind AND
 *     a scope (and vice versa — a window/scope without a cap is dead config and rejected);
 *   - a rolling window requires window_seconds > 0; a non-rolling window must NOT carry seconds.
 *
 * The wire shape is kept EXACT: a threshold with no cumulative cap omits cumulative_max,
 * window and scope entirely; a cumulative threshold emits cumulative_max + window + scope,
 * and window_seconds only for a rolling window. Returns policy=null when every row is blank.
 */
export function draftsToPolicy(
  drafts: ThresholdDraft[],
  dimMeta?: Map<string, DimMeta>,
): DraftsToPolicyResult {
  const errors: Record<string, string> = {};
  const thresholds: ConsequenceThreshold[] = [];
  const seen = new Set<string>();
  drafts.forEach((d, i) => {
    const dim = d.dimension.trim();
    // The cap magnitudes are entered in HUMAN units and scaled to the stored smallest-unit integer via
    // this dimension's exponent; with no meta (or an un-migrated caller) the caps parse as whole
    // integers exactly as before. Keyed by the row's dimension so each row uses its own unit.
    const meta = dimMeta?.get(dim);
    const cap = d.perActionMax.trim();
    const hardCap = (d.perActionHardMax ?? "").trim();
    const cumEmpty = cumulativeSectionEmpty(d);
    // A wholly-blank row (no dimension, no caps, no cumulative section) is dropped.
    if (dim === "" && cap === "" && hardCap === "" && cumEmpty) return;

    if (dim === "") {
      errors[`${i}.dimension`] = "Enter a dimension key.";
    } else if (!dimensionKeyRe.test(dim)) {
      errors[`${i}.dimension`] =
        "Use a lowercase key (a–z, 0–9, _) starting with a letter — e.g. usd.";
    } else if (seen.has(dim)) {
      errors[`${i}.dimension`] = "Duplicate dimension — each may appear once.";
    }
    if (dim !== "" && !errors[`${i}.dimension`]) seen.add(dim);

    // Per-action cap (optional once another cap is set, but if typed it must be valid).
    let perActionMax: number | null = null;
    if (cap !== "") {
      const r = parseHumanCap(cap, meta);
      if (r.error) errors[`${i}.perActionMax`] = r.error;
      else perActionMax = r.minor;
    }

    // Hard per-action ceiling (optional; above it the action is denied even with approval).
    let perActionHardMax: number | null = null;
    if (hardCap !== "") {
      const r = parseHumanCap(hardCap, meta);
      if (r.error) errors[`${i}.perActionHardMax`] = r.error;
      else perActionHardMax = r.minor;
    }
    // Band ordering (the Go invariant): the escalate line must not sit above the hard-deny
    // ceiling, else the action would be denied before it could ever escalate.
    if (perActionMax != null && perActionHardMax != null && perActionMax > perActionHardMax) {
      errors[`${i}.perActionHardMax`] =
        "The hard ceiling must be at least the approval line — above it, actions are denied even with approval.";
    }

    // Cumulative cap section (optional). When any sub-field is set, validate the whole shape.
    let cumulativeMax: number | null = null;
    if (!cumEmpty) {
      const cumStr = (d.cumulativeMax ?? "").trim();
      if (cumStr === "") {
        errors[`${i}.cumulativeMax`] = "Enter a cumulative cap, or clear the window and scope.";
      } else {
        const r = parseHumanCap(cumStr, meta);
        if (r.error) errors[`${i}.cumulativeMax`] = r.error;
        else cumulativeMax = r.minor;
      }
      if (d.windowKind === "") {
        errors[`${i}.windowKind`] = "Choose a window for the cumulative cap.";
      }
      if (d.scope === "") {
        errors[`${i}.scope`] = "Choose a scope for the cumulative cap.";
      }
      if (d.windowKind === "rolling") {
        const secStr = (d.windowSeconds ?? "").trim();
        const sec = Number(secStr);
        if (secStr === "" || !Number.isFinite(sec) || !Number.isInteger(sec) || sec <= 0) {
          errors[`${i}.windowSeconds`] = "Enter a window length greater than zero.";
        }
      }
      // RESET-PROOF SCOPE for a HARD STOP (mirrors Go consequence.Validate; budget-reset gaming
      // remediation Item 1). A hard-stop budget denies outright with no human override, so it must
      // count across a scope a retire+re-provision does NOT reset. The identity-scoped keys — per
      // agent, per grant, per standing delegation — are all minted fresh when an agent is retired
      // and re-provisioned, restoring the full budget against a $0 total. Only "per delegating human"
      // (actor_human) and "per tenant" survive that. Steer the author there rather than let them save
      // a hard wall that a re-provision silently clears. (The reserved agent_calls dimension is the one
      // legitimate exception — it is a platform-synthesized cap, never authored here.)
      if (
        d.cumulativeDeny &&
        dim !== "agent_calls" &&
        (d.scope === "agent" || d.scope === "delegation" || d.scope === "standing_delegation")
      ) {
        errors[`${i}.scope`] =
          "A hard stop must count per delegating human or per tenant. Per-agent, per-grant, and per-standing-delegation budgets reset when an agent is retired and re-provisioned, which clears the running total.";
      }
    }

    // The per-window supplement ceiling (optional; the max a temporary raise may add). Meaningful
    // ONLY with a cumulative cap; a ceiling typed without one is dead config (mirrors Go Validate).
    let maxSupplementPerWindow: number | null = null;
    const suppStr = (d.maxSupplementPerWindow ?? "").trim();
    if (suppStr !== "") {
      if (cumEmpty || (d.cumulativeMax ?? "").trim() === "") {
        errors[`${i}.maxSupplementPerWindow`] =
          "A temporary-increase ceiling only applies to a cumulative budget — set a cumulative cap first.";
      } else {
        const r = parseHumanCap(suppStr, meta);
        if (r.error) errors[`${i}.maxSupplementPerWindow`] = r.error;
        else maxSupplementPerWindow = r.minor;
      }
    }

    // At least one cap is required on a non-blank row (the Go "sets no cap" rule).
    if (dim !== "" && cap === "" && hardCap === "" && cumEmpty) {
      errors[`${i}.perActionMax`] = "Set a per-action cap, a hard ceiling, a cumulative cap, or any mix.";
    }

    // Build the wire threshold only when this row is wholly valid.
    const rowHasError = [
      "dimension",
      "perActionMax",
      "perActionHardMax",
      "cumulativeMax",
      "windowKind",
      "windowSeconds",
      "scope",
      "maxSupplementPerWindow",
    ].some((k) => errors[`${i}.${k}`]);
    if (rowHasError || dim === "") return;
    if (perActionMax == null && perActionHardMax == null && cumulativeMax == null) return; // belt-and-braces

    const threshold: ConsequenceThreshold = { dimension: dim };
    if (perActionMax != null) threshold.per_action_max = perActionMax;
    if (perActionHardMax != null) threshold.per_action_hard_max = perActionHardMax;
    if (cumulativeMax != null) {
      threshold.cumulative_max = cumulativeMax;
      threshold.scope = d.scope;
      threshold.window =
        d.windowKind === "rolling"
          ? { window_kind: "rolling", window_seconds: Number((d.windowSeconds ?? "").trim()) }
          : { window_kind: d.windowKind };
      // Hard stop on breach — only meaningful WITH a cumulative cap (omitted otherwise so a stray
      // toggle on a per-action-only row can't ride onto the wire as dead config).
      if (d.cumulativeDeny) threshold.cumulative_deny = true;
      // The frozen per-window temporary-increase ceiling (only alongside a cumulative cap).
      if (maxSupplementPerWindow != null) threshold.max_supplement_per_window = maxSupplementPerWindow;
    }
    thresholds.push(threshold);
  });
  if (Object.keys(errors).length > 0) return { policy: null, errors };
  if (thresholds.length === 0) return { policy: null, errors };
  return { policy: { thresholds }, errors };
}

/**
 * humanizeDimensionQuantity renders ONE (dimension, smallest-unit-quantity) pair the way the WYSIWYS
 * card does, driven entirely by the dimension's metadata — a known ISO 4217 currency gets its symbol
 * and decimals ("€250,000.00" from 25000000 at exponent 2), every other dimension its number + label
 * ("5,000 records"), and an unknown dimension its raw integer + key. NO hardcoded currency: money is
 * just the dimension whose metadata carries a unit_code. Pass the dimension's DimMeta (from the wire
 * `dimensions` map); omit it only where metadata genuinely isn't available (renders the raw key).
 */
export function humanizeDimensionQuantity(dimension: string, quantity: number, meta?: DimMeta): string {
  // With metadata: render through it (currency symbol/decimals, or "N label") — any unit, no hardcode.
  // Without: a bare grouped integer (unit-agnostic; a caller lacking a dimensions map shows the number,
  // never a fabricated currency). Every authoring/display surface threads the real DimMeta.
  if (meta) return formatValue(quantity, dimension, meta);
  return quantity.toLocaleString();
}

/**
 * toMinorUnits / fromMinorUnits convert between a human-entered value and the dimension's stored
 * SMALLEST-unit integer, driven by the dimension's exponent (a 2-decimal currency ⇒ ×100; a count ⇒
 * ×1). The exponent is the ONE source of the scale factor (from the dimension's DimMeta), so the value
 * the passkey commits to can never drift from what the human saw — and there is no hardcoded currency.
 * A nil meta (or exponent ≤ 0) is treated as a whole-unit count (factor 1).
 */
export function toMinorUnits(meta: DimMeta | undefined, humanValue: number): number {
  const exponent = meta && meta.exponent > 0 ? meta.exponent : 0;
  return Math.round(humanValue * 10 ** exponent);
}

export function fromMinorUnits(meta: DimMeta | undefined, minor: number): number {
  const exponent = meta && meta.exponent > 0 ? meta.exponent : 0;
  return exponent > 0 ? minor / 10 ** exponent : minor;
}

/**
 * minorToDraftString renders a stored smallest-unit integer as the PLAIN, ungrouped string an editor
 * <input> holds — "1000.50" for 100050 at exponent 2, "50000" for a count. Distinct from formatValue
 * (which groups + adds a symbol for DISPLAY): a form field must round-trip losslessly, so no separators
 * and exactly `exponent` decimals. Without meta it is String(minor) — byte-identical to the pre-flip
 * raw-integer draft, so a caller that passes no DimMeta gets the old behavior.
 */
function minorToDraftString(minor: number, meta: DimMeta | undefined): string {
  const exponent = meta && meta.exponent > 0 ? meta.exponent : 0;
  return exponent > 0 ? (minor / 10 ** exponent).toFixed(exponent) : String(minor);
}

/**
 * parseHumanCap validates ONE editor cap string and converts it to the stored smallest-unit integer.
 * WITH meta it accepts a non-negative decimal with at most `exponent` places and scales up by 10^exp
 * (so the human types "1000.50" and the wire carries 100050 — the 100x entry footgun is closed at the
 * source); WITHOUT meta (or exponent 0) it requires a whole non-negative number, byte-identical to the
 * pre-flip parse. Overflow past the JS safe-integer range is rejected rather than silently truncated.
 * Returns {minor:null} for an empty string (the caller decides whether that row-field is required).
 */
function parseHumanCap(raw: string, meta: DimMeta | undefined): { minor: number | null; error?: string } {
  const s = raw.trim();
  if (s === "") return { minor: null };
  const exponent = meta && meta.exponent > 0 ? meta.exponent : 0;
  const wholeOnlyMsg = "Enter a whole, non-negative number.";
  const decimalsMsg = `Use at most ${exponent} decimal place${exponent === 1 ? "" : "s"}.`;
  if (!/^\d+(\.\d+)?$/.test(s)) {
    return { minor: null, error: exponent > 0 ? decimalsMsg : wholeOnlyMsg };
  }
  const dot = s.indexOf(".");
  const decimals = dot === -1 ? 0 : s.length - dot - 1;
  if (decimals > exponent) {
    return { minor: null, error: exponent === 0 ? wholeOnlyMsg : decimalsMsg };
  }
  const minor = toMinorUnits(meta, Number(s));
  if (!Number.isSafeInteger(minor)) return { minor: null, error: "That amount is too large." };
  return { minor };
}

/**
 * A cumulative window humanized: "day" / "month" / "rolling 24h" / "rolling 7 days" /
 * "lifetime". Multi-day rolling windows (≥48h on a whole-day boundary) read as days —
 * "rolling 7 days", not the operator-hostile "rolling 168h"; a single day stays "24h"
 * (the natural phrasing for a daily window).
 */
export function humanizeWindow(window: ConsequenceWindow | undefined): string {
  switch (window?.window_kind) {
    case "calendar_day":
      return "day";
    case "calendar_month":
      return "month";
    case "delegation_lifetime":
      return "lifetime";
    case "rolling": {
      const secs = window.window_seconds ?? 0;
      if (secs >= 2 * 86400 && secs % 86400 === 0) return `rolling ${secs / 86400} days`;
      if (secs > 0 && secs % 3600 === 0) return `rolling ${secs / 3600}h`;
      if (secs > 0 && secs % 60 === 0) return `rolling ${secs / 60}m`;
      return secs > 0 ? `rolling ${secs}s` : "rolling";
    }
    default:
      return "window";
  }
}

// ── Reset-proofing (budget-reset gaming remediation — Item 1 + the escalate-mode trust surface) ──
//
// A cumulative running total is IDENTITY-SCOPED at the DB layer: SUM(quantity) over `scope`.
// Retiring an agent and re-provisioning mints a NEW agent_id / standing_delegation_id, so the
// scopes keyed on those columns read $0 again — the budget silently restarts. Only the two scopes
// keyed on a subject a re-provision does NOT change — the delegating human (actor_human) and the
// tenant — survive a retire+re-provision. A HARD WALL (cumulative_deny) is already FORCED to those
// two scopes on the wire (draftsToPolicy above + Go consequence.Validate), so it is always
// reset-proof. An ESCALATE-mode cap (the default) at an identity scope stays reset-prone: after a
// reset its escalation threshold reads $0 and the successor spends with NO human ever asked. This
// is the residual the anti-silence control carries forward and Item 3 detects — and the verdict
// this trust surface must show the author at a glance.

/** The three reset-proofing states a cumulative cap can be in (or "not_applicable" for no cap). */
export type ResetProofVerdict = "reset_proof" | "reset_prone" | "not_applicable";

/**
 * scopeIsResetProof — true only for the scopes a retire+re-provision does NOT reset. The identity
 * scopes (agent / delegation / standing_delegation) all get a fresh key on re-provision; only
 * actor_human ("per delegating human"), tenant, and workload ("per attested workload") survive it.
 * workload (Tier 3) keys on the agent's attested federated identity — re-provision-stable AND
 * per-agent granular. Mirrors the Go scopeColumn / Validate reset-proof set.
 */
export function scopeIsResetProof(scope: ConsequenceScope | undefined): boolean {
  return scope === "actor_human" || scope === "tenant" || scope === "workload";
}

/**
 * thresholdResetProofVerdict — the reset-proofing verdict for ONE wire threshold:
 *   - no cumulative_max ⇒ "not_applicable" (a per-action-only cap doesn't accrue across resets);
 *   - the reserved, platform-synthesized `agent_calls` cap is never author-reset-gameable ⇒ N/A;
 *   - cumulative_deny (a hard wall) ⇒ "reset_proof" (forced to actor_human/tenant on the wire);
 *   - otherwise reset-proof iff its scope is actor_human/tenant, else "reset_prone".
 */
export function thresholdResetProofVerdict(t: ConsequenceThreshold): ResetProofVerdict {
  if (t.cumulative_max == null) return "not_applicable";
  if (t.dimension === "agent_calls") return "not_applicable";
  if (t.cumulative_deny === true) return "reset_proof";
  return scopeIsResetProof(t.scope) ? "reset_proof" : "reset_prone";
}

/**
 * draftResetProofVerdict — the same verdict computed from an EDITOR row (raw strings), so the
 * author sees the badge live as they pick a scope/mode, before the wire policy is built.
 */
export function draftResetProofVerdict(d: ThresholdDraft): ResetProofVerdict {
  if ((d.cumulativeMax ?? "").trim() === "") return "not_applicable";
  if (d.dimension.trim() === "agent_calls") return "not_applicable";
  if (d.cumulativeDeny) return "reset_proof";
  return scopeIsResetProof(d.scope) ? "reset_proof" : "reset_prone";
}

/**
 * policyResetProofVerdict — the POLICY-level "reset-proof: yes/no" verdict for the list badge:
 *   - "reset_prone" (no) when ANY threshold's running total resets on re-provision — the loud case;
 *   - "reset_proof" (yes) when the policy has ≥1 cumulative cap and NONE is reset-prone;
 *   - "not_applicable" when the policy has no cumulative caps at all (nothing accrues to reset).
 * Derives only from scope + cumulative_deny + cumulative_max inside the thresholds — no dimension
 * metadata needed, so it works off the LIST read (which carries consequence_policy but not dims).
 */
export function policyResetProofVerdict(
  policy: ConsequencePolicy | null | undefined,
): ResetProofVerdict {
  let sawResetProof = false;
  for (const t of policy?.thresholds ?? []) {
    const v = thresholdResetProofVerdict(t);
    if (v === "reset_prone") return "reset_prone";
    if (v === "reset_proof") sawResetProof = true;
  }
  return sawResetProof ? "reset_proof" : "not_applicable";
}

/** A cumulative scope humanized: "per agent" / "per delegation" / "per delegating human" / "per tenant". */
export function humanizeScope(scope: ConsequenceScope | undefined): string {
  switch (scope) {
    case "agent":
      return "per agent";
    case "delegation":
      return "per delegation";
    case "standing_delegation":
      return "per standing delegation";
    case "actor_human":
      return "per delegating human";
    case "tenant":
      return "per tenant";
    default:
      return "";
  }
}

/**
 * policyHasAdvancedRules — true when a policy carries enforcement-bearing rules the form
 * editor cannot represent: qualified (cell) thresholds, a hard-deny cumulative breach
 * (cumulative_deny), or multiple thresholds on one dimension (a base + qualified cell —
 * legal on the wire, unrepresentable as one draft row).
 *
 * Callers MUST fail closed on true: render the policy read-only and refuse to rebuild it
 * from drafts — a policyToDrafts→draftsToPolicy round-trip would silently ERASE the rules
 * (or, for a duplicated dimension, dead-end on the duplicate check). Editing such a policy
 * is an API-level operation until the editor models these rules.
 */
export function policyHasAdvancedRules(policy: ConsequencePolicy | null | undefined): boolean {
  const ths = policy?.thresholds ?? [];
  const seen = new Set<string>();
  for (const t of ths) {
    // cumulative_deny is now first-class in the editor (the hard-stop toggle) and round-trips through
    // drafts, so it is NO LONGER "advanced" — only qualified (cell) rules and multi-threshold
    // dimensions remain un-representable and force the read-only fail-closed.
    if ((t.qualifier?.length ?? 0) > 0) return true;
    if (seen.has(t.dimension)) return true;
    seen.add(t.dimension);
  }
  return false;
}

/** One threshold rendered for display: the dimension plus every cap it carries. */
export interface RenderedThresholdLine {
  dimension: string;
  /** The per-action approval line, humanized (e.g. "$30,000.00"), or null when this threshold has none. */
  perAction: string | null;
  /**
   * The hard per-action ceiling, humanized — the magnitude above which the action is DENIED
   * even with human approval — or null when this threshold has none. A null here on a
   * threshold with a per-action line means approval can authorize ANY amount; surfaces
   * should say so rather than stay silent.
   */
  hardMax: string | null;
  /** The cumulative cap line, humanized (e.g. "$500.00 / day · per delegation"), or null when none. */
  cumulative: string | null;
}

/**
 * renderThresholdLines renders a policy's thresholds with EVERY cap kind, so an operator who
 * set a cumulative-only cap still sees it and a hard-deny ceiling
 * is never silently hidden. A threshold may carry any mix of the three caps — every present
 * cap is rendered. Unit-agnostic: "usd" is humanized as currency, every other dimension as
 * its integer. Returns [] for an empty/absent policy (the caller shows an "escalate all" note).
 *
 * The cumulative line reads e.g. "$500.00 / day · per delegation" — humanized cap, window, scope.
 */
export function renderThresholdLines(
  policy: ConsequencePolicy | null | undefined,
  dimMeta?: Map<string, DimMeta>,
): RenderedThresholdLine[] {
  return (policy?.thresholds ?? [])
    .map((t) => {
      const meta = dimMeta?.get(t.dimension);
      const perAction =
        t.per_action_max != null ? humanizeDimensionQuantity(t.dimension, t.per_action_max, meta) : null;
      const hardMax =
        t.per_action_hard_max != null
          ? humanizeDimensionQuantity(t.dimension, t.per_action_hard_max, meta)
          : null;
      let cumulative: string | null = null;
      if (t.cumulative_max != null) {
        const cap = humanizeDimensionQuantity(t.dimension, t.cumulative_max, meta);
        const win = humanizeWindow(t.window);
        const scope = humanizeScope(t.scope);
        cumulative = scope ? `${cap} / ${win} · ${scope}` : `${cap} / ${win}`;
      }
      return { dimension: t.dimension, perAction, hardMax, cumulative };
    })
    .filter((l) => l.perAction != null || l.hardMax != null || l.cumulative != null);
}

// ── Effective policy for DISPLAY ─────────────────────────────────────────────────────────
//
// Mirrors backend agentpolicy.EffectivePolicy + consequence.Intersect for the shapes the
// admin UI displays (UNQUALIFIED base thresholds — callers gate advanced policies via
// policyHasAdvancedRules first). The mint's enforcement truth lives server-side (and the
// policy-simulate endpoint answers point questions from it); this mirror exists so the
// authority envelope can render the ENFORCED bands, not the raw ceiling, without a network
// round-trip per dimension. Every rule below cites its Go source.

/** One effective display row: the intersected threshold + how it relates to the agent ceiling. */
export interface EffectiveDisplayThreshold {
  threshold: ConsequenceThreshold;
  /**
   * "registry" — the agent ceiling governs as-is; "tightened" — the tenant default lowered
   * a line; "collapsed" — only one layer sizes the dimension, so the step-up band is gone
   * (escalate-all below a surviving hard ceiling: Go intersectDimension → oneSidedHardCeiling,
   * which synthesizes per_action_max 0); "tenant_only" — the surviving rule comes entirely
   * from the tenant default.
   */
  source: "registry" | "tightened" | "collapsed" | "tenant_only";
}

export type EffectiveDisplayPolicy =
  /** The tenant has not turned agent approval thresholds on: the EFFECTIVE policy is empty —
   *  every value-bearing action asks a person; the agent ceiling is dormant (Go
   *  agentpolicy.EffectivePolicy: configured == false ⇒ empty). */
  | { kind: "thresholds_off" }
  /** Thresholds on with no tenant-wide cap (null default): the agent ceiling alone governs. */
  | { kind: "registry_only"; rows: EffectiveDisplayThreshold[] }
  /** Thresholds on with a tenant default: the intersection governs. `dormantDims` are agent
   *  dimensions whose autonomy vanished entirely (one-sided, no hard ceiling to survive);
   *  `conflictDims` have irreconcilable cumulative window/scope — minting REFUSES for them
   *  until aligned (Go composeCumulative fail-closed). */
  | {
      kind: "intersected";
      rows: EffectiveDisplayThreshold[];
      dormantDims: string[];
      conflictDims: string[];
    };

function unqualifiedFor(p: ConsequencePolicy | null | undefined, dim: string): ConsequenceThreshold | null {
  for (const t of p?.thresholds ?? []) {
    if (t.dimension === dim && (t.qualifier?.length ?? 0) === 0) return t;
  }
  return null;
}

function minNullable(a: number | null | undefined, b: number | null | undefined): number | null {
  // Go minI64ptr: nil = unbounded; min ignores nil (one bound beats no bound).
  const av = a ?? null;
  const bv = b ?? null;
  if (av == null && bv == null) return null;
  if (av == null) return bv;
  if (bv == null) return av;
  return Math.min(av, bv);
}

function sameWindow(a: ConsequenceWindow | undefined, b: ConsequenceWindow | undefined): boolean {
  return (
    (a?.window_kind ?? "") === (b?.window_kind ?? "") &&
    (a?.window_seconds ?? 0) === (b?.window_seconds ?? 0)
  );
}

/**
 * effectiveDisplayPolicy — the display-side mirror of the mint's policy resolution:
 *
 *   - configured == false ⇒ thresholds_off (empty effective policy; ceilings dormant);
 *   - tenant default null ⇒ registry_only (distinct nil-sentinel: no tenant constraint);
 *   - otherwise the base×base intersection per dimension (Go intersectDimension):
 *       · both layers size the dim ⇒ per-action + hard lines are the ptr-min of the two;
 *         cumulative caps compose to the min when window+scope MATCH, and CONFLICT
 *         (mint refuses) when both exist and disagree (Go composeCumulative);
 *       · only ONE layer sizes the dim ⇒ the step-up band collapses (escalate-all);
 *         a hard ceiling on that side survives as per_action_max 0 + the ceiling (Go
 *         oneSidedHardCeiling); with no hard ceiling the dimension goes fully dormant.
 */
export function effectiveDisplayPolicy(
  registry: ConsequencePolicy | null | undefined,
  configured: boolean,
  tenantDefault: ConsequencePolicy | null | undefined,
): EffectiveDisplayPolicy {
  if (!configured) return { kind: "thresholds_off" };
  if (tenantDefault == null) {
    return {
      kind: "registry_only",
      rows: (registry?.thresholds ?? [])
        .filter((t) => (t.qualifier?.length ?? 0) === 0)
        .map((t) => ({ threshold: t, source: "registry" as const })),
    };
  }

  const dims = new Set<string>();
  for (const t of registry?.thresholds ?? []) if ((t.qualifier?.length ?? 0) === 0) dims.add(t.dimension);
  for (const t of tenantDefault.thresholds ?? []) if ((t.qualifier?.length ?? 0) === 0) dims.add(t.dimension);

  const rows: EffectiveDisplayThreshold[] = [];
  const dormantDims: string[] = [];
  const conflictDims: string[] = [];

  for (const dim of [...dims].sort()) {
    const reg = unqualifiedFor(registry, dim);
    const td = unqualifiedFor(tenantDefault, dim);

    if (reg != null && td != null) {
      let pa = minNullable(reg.per_action_max, td.per_action_max);
      const hm = minNullable(reg.per_action_hard_max, td.per_action_hard_max);
      // Cross-layer composition can leave pa > hm (each layer valid alone: one supplies a
      // generous ask line, the other a tight hard ceiling). Enforcement checks the hard
      // deny BEFORE escalation, so the observable behavior is autonomous ≤ hm, denied
      // above — exactly the pa === hm shape. Normalize so the bands never claim an ask
      // band that cannot exist.
      if (pa != null && hm != null && pa > hm) pa = hm;
      const eff: ConsequenceThreshold = { dimension: dim };
      if (pa != null) eff.per_action_max = pa;
      if (hm != null) eff.per_action_hard_max = hm;
      const regCum = reg.cumulative_max ?? null;
      const tdCum = td.cumulative_max ?? null;
      if (regCum != null && tdCum != null) {
        if (sameWindow(reg.window, td.window) && (reg.scope ?? "") === (td.scope ?? "")) {
          eff.cumulative_max = Math.min(regCum, tdCum);
          eff.window = reg.window;
          eff.scope = reg.scope;
        } else {
          // The layers disagree on the cumulative window/scope: the mint FAILS CLOSED for
          // this agent (composeCumulative error → the mint caller refuses), so rendering
          // bands would claim autonomy that cannot be exercised — no row is emitted; the
          // caller shows the conflict loudly instead.
          conflictDims.push(dim);
          continue;
        }
      } else if (regCum != null || tdCum != null) {
        const src = regCum != null ? reg : td;
        eff.cumulative_max = src.cumulative_max;
        eff.window = src.window;
        eff.scope = src.scope;
      }
      const tightened =
        (reg.per_action_max ?? null) !== pa ||
        (reg.per_action_hard_max ?? null) !== hm ||
        (reg.cumulative_max ?? null) !== (eff.cumulative_max ?? null);
      rows.push({ threshold: eff, source: tightened ? "tightened" : "registry" });
      continue;
    }

    const only = reg ?? td;
    if (only == null) continue;
    if (only.per_action_hard_max != null) {
      // One-sided hard ceiling survives as escalate-all-below + hard-deny-above.
      rows.push({
        threshold: { dimension: dim, per_action_max: 0, per_action_hard_max: only.per_action_hard_max },
        source: reg != null ? "collapsed" : "tenant_only",
      });
    } else if (reg != null) {
      dormantDims.push(dim); // the agent ceiling here grants nothing at mint
    }
    // A tenant-only dim with no hard ceiling constrains nothing visible for this agent.
  }

  return { kind: "intersected", rows, dormantDims, conflictDims };
}

/**
 * renderMeasurements renders a runtime consequence's measurements as "dimension: quantity"
 * pairs for display (the approval-history row). Unit-agnostic. [] for a no-cost action.
 */
export function renderMeasurements(
  c: Consequence | null | undefined,
): Array<{ dimension: string; quantity: string }> {
  return (c?.measurements ?? []).map((m) => ({
    dimension: m.dimension,
    quantity: m.quantity.toLocaleString(),
  }));
}

// usdMinorUnitDigits derives USD's fraction-digit exponent from Intl (authoritative
// per ISO 4217 — USD=2). Returns null only if the runtime cannot format USD at all,
// in which case the caller FAILS CLOSED to showing the raw integer rather than
// guessing a divisor and misleading the human about a magnitude they're signing.
function usdMinorUnitDigits(): number | null {
  try {
    return (
      new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).resolvedOptions()
        .maximumFractionDigits ?? null
    );
  } catch {
    return null;
  }
}

/**
 * A single measurement rendered for a HIGH-STAKES human-facing surface (the WYSIWYS
 * approval card): a human-friendly `human` value plus the always-available raw
 * `rawQuantity` integer so the generic (dimension, quantity) wire stays visible.
 */
export interface RenderedMeasurement {
  dimension: string;
  human: string;
  rawQuantity: string;
}

/**
 * renderMeasurementHuman turns ONE wire measurement into its display form for the
 * WYSIWYS card. The "usd" dimension is formatted as real currency from minor units
 * ($250,000.00 from 25000000); EVERY other dimension is rendered generically as its
 * integer quantity (the product is unit-agnostic — money is one dimension, NOT the
 * only path). Returns null for a measurement whose shape cannot be rendered at all
 * (non-finite/negative quantity, or an empty/whitespace dimension after sanitizing)
 * — the fail-closed signal a caller uses to DISABLE signing what cannot be seen.
 *
 * `sanitize` lets a caller strip control/bidi/zero-width chars from the (operator-
 * entered, but defensively treated as untrusted) dimension key before display.
 */
export function renderMeasurementHuman(
  m: Measurement,
  sanitize: (s: string) => string = (s) => s,
  meta?: DimMeta,
): RenderedMeasurement | null {
  if (!Number.isFinite(m.quantity) || m.quantity < 0) return null;
  const dimension = sanitize(m.dimension).trim();
  if (dimension === "") return null;
  const rawQuantity = m.quantity.toLocaleString();
  // Preferred path: render through the dimension's metadata (any currency/count, no hardcode).
  if (meta) {
    return { dimension, human: formatValue(m.quantity, dimension, meta), rawQuantity };
  }
  // Fallback for callers that don't yet carry a dimensions map (tracked): usd as currency, else raw.
  if (m.dimension === "usd") {
    const digits = usdMinorUnitDigits();
    if (digits === null) {
      return { dimension, human: `${rawQuantity} (minor units)`, rawQuantity };
    }
    return {
      dimension,
      human: new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(
        m.quantity / 10 ** digits,
      ),
      rawQuantity,
    };
  }
  return { dimension, human: rawQuantity, rawQuantity };
}

/**
 * renderConsequenceHuman renders every renderable measurement a consequence carries
 * (see renderMeasurementHuman). Unrenderable measurements are dropped from the list
 * but surfaced by consequenceIsRenderable so the caller can fail closed.
 */
export function renderConsequenceHuman(
  c: Consequence | null | undefined,
  sanitize?: (s: string) => string,
  dimMeta?: Map<string, DimMeta>,
): RenderedMeasurement[] {
  return (c?.measurements ?? [])
    .map((m) => renderMeasurementHuman(m, sanitize, dimMeta?.get(m.dimension)))
    .filter((r): r is RenderedMeasurement => r !== null);
}

/**
 * consequenceIsRenderable is true only if EVERY measurement the consequence carries
 * can be displayed (see renderMeasurementHuman). A measurement the action_hash
 * commits to but which renders to nothing would mean the human signs a magnitude
 * unseen — the caller must fail closed (disable Approve).
 */
export function consequenceIsRenderable(
  c: Consequence | null | undefined,
  sanitize?: (s: string) => string,
): boolean {
  return (c?.measurements ?? []).every((m) => renderMeasurementHuman(m, sanitize) !== null);
}
